import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { createOpenAIClient, generateMultipleAnswers, generateFollowUpQuestions, generateComprehensiveAnswer, AnswerPerspective } from '../utils/openai';
import { ConversationNode } from '../types';
import QuestionInput from './QuestionInput';
import LoadingSpinner from './LoadingSpinner';

interface MainContentProps {
  isConnected: boolean;
  apiKey: string;
}

interface ConversationNodeComponentProps {
  node: ConversationNode;
  onFollowUpClick: (question: string, parentNode: ConversationNode) => void;
  onPerspectiveFollowUpClick: (question: string, nodeId: string, perspectiveId: string) => void;
  onToggleExpanded: (nodeId: string) => void;
  onAnswerToggle?: (nodeId: string, answerId: string) => void;
}

const ConversationNodeComponent: React.FC<ConversationNodeComponentProps> = ({
  node,
  onFollowUpClick,
  onPerspectiveFollowUpClick,
  onToggleExpanded,
  onAnswerToggle
}) => {
  const indentLevel = node.depth;
  
  // Cap the visual indentation at 3 levels to prevent width issues
  const visualIndentLevel = Math.min(indentLevel, 3);
  
  // Use a more conservative indentation that doesn't eat up too much width
  const indentStyle = {
    marginLeft: `${visualIndentLevel * 0}px`,
    paddingLeft: indentLevel > 0 ? '12px' : '0',
    position: 'relative' as const
  };

  // Add depth indicator for levels beyond visual indentation
  const depthIndicator = indentLevel > 3 ? (
    <div className="depth-indicator">
      Level {indentLevel}
    </div>
  ) : null;

  return (
    <div 
      className="conversation-node-container" 
      style={indentStyle}
      data-depth={indentLevel}
    >
      <div className={`conversation-node depth-${Math.min(indentLevel, 4)}`}>
        {depthIndicator}
        
        {/* Question Header */}
        <div className="conversation-question">
          <div className="conversation-label">
            {indentLevel === 0 ? 'Question:' : 'Follow-up:'}
          </div>
          <div className="conversation-text">{node.question}</div>
          <div className="conversation-timestamp">
            {node.timestamp.toLocaleTimeString()}
          </div>
        </div>

        {/* Loading State for Answer */}
        {node.isLoadingAnswer && (
          <div className="loading-section loading-expansion">
            <LoadingSpinner 
              message={indentLevel === 0 ? "Generating follow ups..." : "Diving deeper..."} 
              size="medium" 
              variant={indentLevel === 0 ? "secondary" : "accent"}
            />
          </div>
        )}

        {/* Render Children Immediately After Question - This is the key change */}
        {node.children.length > 0 && (
          <div className="conversation-children">
            {node.children.map((childNode) => (
              <ConversationNodeComponent
                key={childNode.id}
                node={childNode}
                onFollowUpClick={onFollowUpClick}
                onPerspectiveFollowUpClick={onPerspectiveFollowUpClick}
                onToggleExpanded={onToggleExpanded}
                onAnswerToggle={onAnswerToggle}
              />
            ))}
          </div>
        )}

        {/* Multiple Answers (for initial questions) */}
        {node.answers && node.answers.length > 0 && (
          <div className="conversation-answers">
            <div className="conversation-label">10 Follow Ups:</div>
            <div className="answers-grid">
              {node.answers.map((answer, index) => (
                <div 
                  key={answer.id} 
                  className={`answer-card ${answer.isExpanded ? 'expanded' : ''} clickable-card`}
                  onClick={() => onAnswerToggle?.(node.id, answer.id)}
                  title="Click to expand and explore this follow-up"
                >
                  <div className="answer-text">
                    <span className="answer-number">{index + 1}.</span> {answer.isExpanded ? answer.text : `${answer.text.substring(0, 300)}${answer.text.length > 300 ? '...' : ''}`}
                  </div>
                  <div className="click-hint">
                    {answer.isExpanded ? '▼ Click to collapse' : '▶ Click to explore'}
                  </div>
                  
                  {/* Follow-up Questions for Selected Answer */}
                  {answer.isExpanded && (
                    <div className="follow-up-section">
                      <div className="follow-up-label">Explore Further:</div>
                      {answer.isLoadingFollowUps ? (
                        <div className="follow-up-loading">
                          <LoadingSpinner 
                            message="Creating follow-up questions..." 
                            size="small" 
                            variant="accent"
                          />
                        </div>
                      ) : answer.followUpQuestions.length > 0 ? (
                        <div className="follow-up-questions-with-trees">
                          {answer.followUpQuestions.map((question, qIndex) => {
                            const isActive = answer.activeFollowUp === question.trim();
                            return (
                              <div key={qIndex} className="follow-up-question-container">
                                <button
                                  className={`follow-up-question ${isActive ? 'active' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onPerspectiveFollowUpClick(question, node.id, answer.id);
                                  }}
                                >
                                  {question}
                                  {isActive && <span className="active-indicator"> ✓</span>}
                                </button>
                                
                                {/* Only render nested conversation for the active follow-up */}
                                {isActive && answer.children && answer.children.length > 0 && (
                                  answer.children
                                    .filter(child => child.question.trim() === question.trim())
                                    .map((childNode) => (
                                      <div 
                                        key={childNode.id} 
                                        className="nested-conversation"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <ConversationNodeComponent
                                          node={childNode}
                                          onFollowUpClick={onFollowUpClick}
                                          onPerspectiveFollowUpClick={onPerspectiveFollowUpClick}
                                          onToggleExpanded={onToggleExpanded}
                                          onAnswerToggle={onAnswerToggle}
                                        />
                                      </div>
                                    ))
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Single Answer (for follow-up questions) */}
        {node.answer && (
          <div className="conversation-single-answer">
            <div className="conversation-label">Answer:</div>
            <div className="single-answer-text">{node.answer}</div>
          </div>
        )}

        {/* Follow-up Questions Section for single answers */}
        {node.followUpQuestions.length > 0 && !node.isLoadingAnswer && node.answer && (
          <div className="node-follow-up-section">
            <div className="follow-up-label">
              {node.isExpanded ? 'Hide Follow-up Questions' : 'Show Follow-up Questions'}
              <button 
                className="toggle-follow-ups"
                onClick={() => onToggleExpanded(node.id)}
              >
                {node.isExpanded ? '▼' : '▶'}
              </button>
            </div>
            
            {node.isExpanded && (
              <div className="follow-up-questions">
                {node.followUpQuestions.map((question, index) => (
                  <button
                    key={index}
                    className="follow-up-question"
                    onClick={() => onFollowUpClick(question, node)}
                  >
                    {question}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Loading Follow-up Questions */}
        {node.isLoadingFollowUps && (
          <div className="loading-section loading-followup">
            <LoadingSpinner 
              message="Creating follow-up questions..." 
              size="medium" 
              variant="accent"
            />
          </div>
        )}
      </div>
    </div>
  );
};

const MainContent: React.FC<MainContentProps> = ({ isConnected, apiKey }) => {
  const [conversationTree, setConversationTree] = useState<ConversationNode[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Helper function to find a node by ID in the tree
  const findNodeById = (nodes: ConversationNode[], nodeId: string): ConversationNode | null => {
    for (const node of nodes) {
      if (node.id === nodeId) {
        return node;
      }
      const found = findNodeById(node.children, nodeId);
      if (found) return found;
      
      // Also search in perspective children
      if (node.answers) {
        for (const answer of node.answers) {
          if (answer.children) {
            const foundInPerspective = findNodeById(answer.children, nodeId);
            if (foundInPerspective) return foundInPerspective;
          }
        }
      }
    }
    return null;
  };

  // Helper function to update a node in the tree
  const updateNodeInTree = (
    nodes: ConversationNode[], 
    nodeId: string, 
    updater: (node: ConversationNode) => ConversationNode
  ): ConversationNode[] => {
    return nodes.map(node => {
      if (node.id === nodeId) {
        return updater(node);
      }
      
      // Update in regular children
      const updatedNode = {
        ...node,
        children: updateNodeInTree(node.children, nodeId, updater)
      };
      
      // Update in perspective children
      if (node.answers) {
        updatedNode.answers = node.answers.map(answer => ({
          ...answer,
          children: answer.children ? updateNodeInTree(answer.children, nodeId, updater) : []
        }));
      }
      
      return updatedNode;
    });
  };

  // Helper function to add a child node to a parent
  const addChildToNode = (
    nodes: ConversationNode[], 
    parentId: string, 
    childNode: ConversationNode
  ): ConversationNode[] => {
    return updateNodeInTree(nodes, parentId, (parent) => ({
      ...parent,
      children: [...parent.children, childNode]
    }));
  };

  // Helper function to add a child node to a specific perspective
  const addChildToPerspective = (
    nodes: ConversationNode[], 
    nodeId: string, 
    perspectiveId: string, 
    childNode: ConversationNode
  ): ConversationNode[] => {
    return nodes.map(node => {
      if (node.id === nodeId && node.answers) {
        return {
          ...node,
          answers: node.answers.map(answer => 
            answer.id === perspectiveId 
              ? { ...answer, children: [...(answer.children || []), childNode] }
              : answer
          )
        };
      }
      
      // Recursively search in children
      const updatedNode = {
        ...node,
        children: addChildToPerspective(node.children, nodeId, perspectiveId, childNode)
      };
      
      // Recursively search in perspective children
      if (node.answers) {
        updatedNode.answers = node.answers.map(answer => ({
          ...answer,
          children: answer.children ? addChildToPerspective(answer.children, nodeId, perspectiveId, childNode) : []
        }));
      }
      
      return updatedNode;
    });
  };

  const handlePerspectiveFollowUpClick = async (followUpQuestion: string, nodeId: string, perspectiveId: string) => {
    // Find the parent node and perspective
    const parentNode = findNodeById(conversationTree, nodeId);
    const perspective = parentNode?.answers?.find(a => a.id === perspectiveId);
    
    if (!parentNode || !perspective) return;

    // Check if this follow-up question is already active
    const existingChild = perspective.children?.find(child => child.question.trim() === followUpQuestion.trim());
    
    if (existingChild) {
      // If the follow-up already exists, remove it (toggle off)
      setConversationTree(prev => 
        updateNodeInTree(prev, nodeId, (node) => ({
          ...node,
          answers: node.answers?.map(answer => 
            answer.id === perspectiveId 
              ? { 
                  ...answer, 
                  children: answer.children?.filter(child => child.id !== existingChild.id) || [],
                  activeFollowUp: null
                }
              : answer
          ) || []
        }))
      );
      return;
    }

    // Clear any existing follow-ups for this perspective and create new one
    const newChildNode: ConversationNode = {
      id: uuidv4(),
      question: followUpQuestion.trim(),
      followUpQuestions: [],
      children: [],
      timestamp: new Date(),
      depth: parentNode.depth + 1,
      isExpanded: false,
      isLoadingFollowUps: false,
      isLoadingAnswer: true
    };

    try {
      // Replace any existing children with just the new one and set as active follow-up
      setConversationTree(prev => {
        const updatedTree = updateNodeInTree(prev, nodeId, (node) => ({
          ...node,
          answers: node.answers?.map(answer => 
            answer.id === perspectiveId 
              ? { 
                  ...answer, 
                  children: [newChildNode], // Replace all children with just this one
                  isExpanded: true,
                  activeFollowUp: followUpQuestion.trim()
                }
              : answer
          ) || []
        }));
        return updatedTree;
      });

      // Generate multiple answers (10 perspectives) for the follow-up question
      const client = createOpenAIClient(apiKey);
      
      // Build context from parent conversation and selected perspective
      let contextualQuestion = followUpQuestion;
      if (parentNode.question && perspective.text) {
        contextualQuestion = `Context: Original question was "${parentNode.question}" and the selected perspective was "${perspective.text}". Now, ${followUpQuestion}`;
      }

      const answers = await generateMultipleAnswers(client, contextualQuestion, {
        model: 'gpt-3.5-turbo',
        maxTokens: 2000,
        temperature: 0.8
      });

      // Update the child node with the multiple answers
      setConversationTree(prev => 
        updateNodeInTree(prev, newChildNode.id, (node) => ({
          ...node,
          answers,
          isLoadingAnswer: false,
          isExpanded: false // Don't auto-expand, let user choose perspective
        }))
      );

    } catch (error) {
      console.error('Error handling perspective follow-up question:', error);
      
      // Update to remove loading state and show error
      setConversationTree(prev => 
        updateNodeInTree(prev, newChildNode.id, (node) => ({
          ...node,
          answer: 'Error generating answer. Please try again.',
          isLoadingAnswer: false
        }))
      );
      
      // Set error message
      if (error instanceof Error) {
        if (error.message.includes('Invalid API key')) {
          setError('Invalid API key. Please check your OpenAI API key and try again.');
        } else if (error.message.includes('Rate limit')) {
          setError('Rate limit exceeded. Please wait a moment and try again.');
        } else {
          setError(`Error: ${error.message}`);
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    }
  };

  const handleAnswerToggle = async (nodeId: string, answerId: string) => {
    // Find the selected answer and node first
    const currentNode = findNodeById(conversationTree, nodeId);
    const selectedAnswer = currentNode?.answers?.find(answer => answer.id === answerId);
    
    if (!selectedAnswer || !currentNode) return;

    // Determine if we're expanding or collapsing
    const isExpanding = !selectedAnswer.isExpanded;

    // Update the UI - only collapse other answers if we're expanding this one
    setConversationTree(prev => 
      updateNodeInTree(prev, nodeId, (node) => ({
        ...node,
        answers: node.answers?.map(answer => ({
          ...answer,
          isExpanded: answer.id === answerId ? !answer.isExpanded : (isExpanding ? false : answer.isExpanded)
        })) || []
      }))
    );

    // If the answer is being expanded and doesn't have follow-up questions yet
    if (isExpanding && selectedAnswer.followUpQuestions.length === 0) {
      // Set loading state for follow-up questions
      setConversationTree(prev => 
        updateNodeInTree(prev, nodeId, (node) => ({
          ...node,
          answers: node.answers?.map(answer =>
            answer.id === answerId
              ? { ...answer, isLoadingFollowUps: true }
              : answer
          ) || []
        }))
      );

      try {
        // Generate follow-up questions
        const client = createOpenAIClient(apiKey);
        const followUpQuestions = await generateFollowUpQuestions(
          client, 
          currentNode.question, 
          selectedAnswer.text
        );

        // Update the answer with follow-up questions
        setConversationTree(prev => 
          updateNodeInTree(prev, nodeId, (node) => ({
            ...node,
            answers: node.answers?.map(answer =>
              answer.id === answerId
                ? { 
                    ...answer, 
                    followUpQuestions,
                    isLoadingFollowUps: false
                  }
                : answer
            ) || []
          }))
        );
      } catch (error) {
        console.error('Error generating follow-up questions:', error);
        // Update to remove loading state
        setConversationTree(prev => 
          updateNodeInTree(prev, nodeId, (node) => ({
            ...node,
            answers: node.answers?.map(answer =>
              answer.id === answerId
                ? { ...answer, isLoadingFollowUps: false }
                : answer
            ) || []
          }))
        );
      }
    }
  };

  const handleFollowUpClick = async (followUpQuestion: string, parentNode: ConversationNode) => {
    // Create new child node
    const newChildNode: ConversationNode = {
      id: uuidv4(),
      question: followUpQuestion.trim(),
      followUpQuestions: [],
      children: [],
      timestamp: new Date(),
      depth: parentNode.depth + 1,
      isExpanded: false,
      isLoadingFollowUps: false,
      isLoadingAnswer: true
    };

    try {
      // Add the child node to the tree and ensure parent is expanded
      setConversationTree(prev => {
        const treeWithChild = addChildToNode(prev, parentNode.id, newChildNode);
        // Also ensure the parent node is expanded to show the new child
        return updateNodeInTree(treeWithChild, parentNode.id, (parent) => ({
          ...parent,
          isExpanded: true
        }));
      });

      // Generate multiple answers (10 perspectives) for the follow-up question
      const client = createOpenAIClient(apiKey);
      
      // Build context from parent conversation
      let contextualQuestion = followUpQuestion;
      if (parentNode.question) {
        if (parentNode.answer) {
          contextualQuestion = `Context: Original question was "${parentNode.question}" and the answer was "${parentNode.answer}". Now, ${followUpQuestion}`;
        } else if (parentNode.answers) {
          const expandedAnswer = parentNode.answers.find(a => a.isExpanded);
          if (expandedAnswer) {
            contextualQuestion = `Context: Original question was "${parentNode.question}" and the selected perspective was "${expandedAnswer.text}". Now, ${followUpQuestion}`;
          }
        }
      }

      const answers = await generateMultipleAnswers(client, contextualQuestion, {
        model: 'gpt-3.5-turbo',
        maxTokens: 2000,
        temperature: 0.8
      });

      // Update the child node with the multiple answers
      setConversationTree(prev => 
        updateNodeInTree(prev, newChildNode.id, (node) => ({
          ...node,
          answers,
          isLoadingAnswer: false,
          isExpanded: false // Don't auto-expand, let user choose perspective
        }))
      );

    } catch (error) {
      console.error('Error handling follow-up question:', error);
      
      // Update to remove loading state and show error
      setConversationTree(prev => 
        updateNodeInTree(prev, newChildNode.id, (node) => ({
          ...node,
          answer: 'Error generating answer. Please try again.',
          isLoadingAnswer: false
        }))
      );
      
      // Set error message
      if (error instanceof Error) {
        if (error.message.includes('Invalid API key')) {
          setError('Invalid API key. Please check your OpenAI API key and try again.');
        } else if (error.message.includes('Rate limit')) {
          setError('Rate limit exceeded. Please wait a moment and try again.');
        } else {
          setError(`Error: ${error.message}`);
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    }
  };

  const handleToggleExpanded = (nodeId: string) => {
    setConversationTree(prev => 
      updateNodeInTree(prev, nodeId, (node) => ({
        ...node,
        isExpanded: !node.isExpanded
      }))
    );
  };

  const handleQuestionSubmit = async (question: string) => {
    if (!apiKey || !isConnected) {
      setError('Please connect your API key first');
      return;
    }

    // Clear previous conversation tree when starting a new question
    setConversationTree([]);
    setIsLoading(true);
    setError(null);

    try {
      // Create OpenAI client
      const client = createOpenAIClient(apiKey);
      
      // Call generateMultipleAnswers to get 10 different perspectives
      const answers = await generateMultipleAnswers(client, question, {
        model: 'gpt-3.5-turbo',
        maxTokens: 2000,
        temperature: 0.8
      });

      // Create new conversation node
      const newNode: ConversationNode = {
        id: uuidv4(),
        question: question.trim(),
        answers: answers,
        followUpQuestions: [],
        children: [],
        timestamp: new Date(),
        depth: 0,
        isExpanded: false,
        isLoadingFollowUps: false,
        isLoadingAnswer: false
      };

      // Replace conversation tree with new node
      setConversationTree([newNode]);

    } catch (error) {
      console.error('Error calling OpenAI API:', error);
      
      // Handle different types of errors with user-friendly messages
      if (error instanceof Error) {
        if (error.message.includes('Invalid API key')) {
          setError('Invalid API key. Please check your OpenAI API key and try again.');
        } else if (error.message.includes('Rate limit')) {
          setError('Rate limit exceeded. Please wait a moment and try again.');
        } else if (error.message.includes('timeout')) {
          setError('Request timed out. Please check your connection and try again.');
        } else if (error.message.includes('network')) {
          setError('Network error. Please check your internet connection.');
        } else {
          setError(`Error: ${error.message}`);
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="main-content">
      <div className="content-container">
        {isConnected ? (
          <>
            <QuestionInput 
              isConnected={isConnected} 
              onQuestionSubmit={handleQuestionSubmit}
              isLoading={isLoading}
            />
            
            {/* Main loading state for initial question submission */}
            {isLoading && (
              <div className="loading-section loading-perspectives">
                <LoadingSpinner 
                  message="Generating 10 follow ups..." 
                  size="large" 
                  variant="primary"
                />
                <div className="progress-indicator">
                  <div className="progress-dots">
                    <div className="progress-dot"></div>
                    <div className="progress-dot"></div>
                    <div className="progress-dot"></div>
                  </div>
                </div>
              </div>
            )}
            
            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                {error}
                <button 
                  className="error-dismiss"
                  onClick={() => setError(null)}
                >
                  ×
                </button>
              </div>
            )}

            {conversationTree.length > 0 && (
              <div className="conversation-tree">
                <h3 className="conversation-title">Conversation Tree</h3>
                <div className="conversation-tree-container">
                  {conversationTree.map((node) => (
                    <ConversationNodeComponent
                      key={node.id}
                      node={node}
                      onFollowUpClick={handleFollowUpClick}
                      onPerspectiveFollowUpClick={handlePerspectiveFollowUpClick}
                      onToggleExpanded={handleToggleExpanded}
                      onAnswerToggle={handleAnswerToggle}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : null}
      </div>
    </main>
  );
};

export default MainContent; 